import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";

import useEmblaCarousel from "embla-carousel-react";

import type { PlanCurrency, PlanId } from "shared/api/tribute/types";

import { Layout } from "pages/ui/page";
import {
  clearAwaitingPayment,
  detectCurrency,
  formatPlanPrice,
  isAwaitingPaymentPersisted,
  isSubscriptionActive,
  markAwaitingPayment,
  openPaymentUrl,
} from "shared/api/tribute/helpers";
import { useCancelSubscriptionMutation } from "shared/api/tribute/use-cancel-mutation";
import { usePlansQuery } from "shared/api/tribute/use-plans-query";
import { useSubscribeMutation } from "shared/api/tribute/use-subscribe-mutation";
import { useSubscriptionQuery } from "shared/api/tribute/use-subscription-query";
import { Breadcrumbs } from "shared/ui/breadcrumbs";
import { Button } from "shared/ui/button";
import { ConfirmDialog } from "shared/ui/confirm-dialog";
import { CurrencySelect } from "shared/ui/currency-select";
import { cn } from "shared/utils/cn";

import { BenefitsSlide } from "../../entities/benefits/benefits-slide";
import { MoreBenefitsSlide } from "../../entities/benefits/more-benefits-slide";

// Slide order mirrors the plan order (and the upgrade direction).
const SLIDE_PLANS: PlanId[] = ["standard", "pro"];

const shortPlanName = (planId: PlanId) =>
  planId === "pro" ? "Pro" : "Standard";

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

// Stop polling for the payment result after this long.
const AWAIT_PAYMENT_TIMEOUT_MS = 5 * 60 * 1000;

export const Subscription = () => {
  const plansQuery = usePlansQuery();
  // Restored from localStorage: opening the Tribute checkout closes the mini
  // app, so a fresh launch resumes waiting for the payment result.
  const [isAwaitingPayment, setIsAwaitingPayment] = useState(
    isAwaitingPaymentPersisted
  );
  const subscriptionQuery = useSubscriptionQuery({
    refetchInterval: isAwaitingPayment ? 3000 : false,
  });

  const [currency, setCurrency] = useState<PlanCurrency>(detectCurrency);

  const subscription = subscriptionQuery.data;
  const plans = plansQuery.data;

  const activePlanId: PlanId | null = useMemo(() => {
    if (!subscription) return null;
    if (
      subscription.subscription !== "standard" &&
      subscription.subscription !== "pro"
    ) {
      return null;
    }
    const expired =
      subscription.expiresAt !== null &&
      new Date(subscription.expiresAt).getTime() < Date.now();
    return expired ? null : subscription.subscription;
  }, [subscription]);

  const startIndex = Math.max(
    0,
    activePlanId ? SLIDE_PLANS.indexOf(activePlanId) : 0
  );

  const [activeSlide, setActiveSlide] = useState(startIndex);
  const [emblaRef, emblaApi] = useEmblaCarousel({ startIndex });
  const [isCancelOpen, setIsCancelOpen] = useState(false);

  const subscribeMutation = useSubscribeMutation();
  const cancelMutation = useCancelSubscriptionMutation();

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", () => setActiveSlide(emblaApi.selectedScrollSnap()));
  }, [emblaApi]);

  useEffect(() => {
    if (!isAwaitingPayment) return;
    const timeout = setTimeout(() => {
      setIsAwaitingPayment(false);
      clearAwaitingPayment();
    }, AWAIT_PAYMENT_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [isAwaitingPayment]);

  const isActivated = isSubscriptionActive(subscription);
  useEffect(() => {
    // Payment confirmed — stop waiting; the queries already reflect the
    // new plan.
    if (isAwaitingPayment && isActivated) {
      setIsAwaitingPayment(false);
      clearAwaitingPayment();
    }
  }, [isAwaitingPayment, isActivated]);

  if (plansQuery.isPending || subscriptionQuery.isPending) {
    return <Layout isLoading />;
  }

  const slidePlanId = SLIDE_PLANS[activeSlide];
  const slidePlan = plans?.plans.find((p) => p.id === slidePlanId);
  const price = formatPlanPrice(slidePlan, currency);

  const paymentsEnabled = Boolean(plans?.paymentsEnabled);
  const trialEligible = !subscription?.expiresAt;
  const isCurrentPlan = activePlanId === slidePlanId;
  const isCancelled = subscription?.order?.memberStatus === "cancelled";

  const handleSubscribe = async () => {
    try {
      const result = await subscribeMutation.mutateAsync({
        plan: slidePlanId,
        currency,
      });
      // Persist BEFORE opening: the checkout closes this mini app.
      markAwaitingPayment();
      setIsAwaitingPayment(true);
      openPaymentUrl(result.webappPaymentUrl, result.paymentUrl);
    } catch (error) {
      console.error(error);
    }
  };

  const handleCancel = async () => {
    try {
      await cancelMutation.mutateAsync();
    } catch (error) {
      console.error(error);
    } finally {
      setIsCancelOpen(false);
    }
  };

  const isUpgrade =
    activePlanId !== null &&
    SLIDE_PLANS.indexOf(slidePlanId) > SLIDE_PLANS.indexOf(activePlanId);

  const subscribeLabel = isCurrentPlan
    ? "This is your subscription"
    : activePlanId
      ? `${isUpgrade ? "Upgrade" : "Downgrade"} to ${shortPlanName(slidePlanId)} · ${price}/m`
      : trialEligible
        ? `Start 7-day free trial · then ${price}/m`
        : `Subscribe · ${price}/m`;

  return (
    <Layout>
      <Breadcrumbs>
        <Breadcrumbs.Item as={Link} to="/settings">
          Settings
        </Breadcrumbs.Item>
        <Breadcrumbs.Item isActive>Subscription</Breadcrumbs.Item>
      </Breadcrumbs>

      <div
        className="mt-3 overflow-hidden rounded-xl"
        style={{
          background:
            "linear-gradient(180deg, rgba(97, 201, 255, 0.12) 0%, rgba(71, 170, 255, 0.12) 100%)",
        }}
        ref={emblaRef}
      >
        <div className="flex cursor-grab select-none">
          <BenefitsSlide />
          <MoreBenefitsSlide />
        </div>
      </div>

      <div className="mt-3 flex justify-center gap-x-1.5">
        {SLIDE_PLANS.map((planId, i) => (
          <button
            key={planId}
            className={cn(
              "size-2 rounded-full transition-colors",
              i === activeSlide ? "bg-crystal-blue" : "bg-white/30"
            )}
            onClick={() => emblaApi?.scrollTo(i)}
          />
        ))}
      </div>

      {activePlanId && (
        <p className="text-pale-grey mt-3 text-sm leading-4.5 tracking-[0.23px]">
          {isCancelled
            ? `Your ${activePlanId} plan is cancelled and stays active until ${formatDate(subscription?.order?.memberExpiresAt ?? subscription?.expiresAt)}.`
            : subscription?.order?.memberInTrial
              ? `Free trial until ${formatDate(subscription?.order?.memberTrialEndsAt)}.`
              : `Your ${activePlanId} plan renews on ${formatDate(subscription?.order?.memberExpiresAt ?? subscription?.expiresAt)}.`}
        </p>
      )}

      <p className="text-pale-grey mt-5 mb-3 text-sm leading-4.5 tracking-[0.23px]">
        By subscribing to Resonar AI, you agree to Resonar AI's{" "}
        <a href="#" target="_blank" className="text-clear-blue">
          Terms of Service.
        </a>
      </p>

      <div className="mt-auto flex flex-col pt-4">
        {!paymentsEnabled && (
          <p className="text-pale-grey mb-2 text-center text-sm">
            Payments are temporarily unavailable.
          </p>
        )}
        {paymentsEnabled && (
          <div className="flex gap-x-2">
            <CurrencySelect value={currency} onChange={setCurrency} />
            <Button
              size="52"
              className="min-w-0 flex-1 text-lg leading-5.5"
              isLoading={subscribeMutation.isPending}
              disabled={isCurrentPlan && !isCancelled}
              onClick={handleSubscribe}
            >
              {isCurrentPlan && isCancelled
                ? `Resume · ${price}/m`
                : subscribeLabel}
            </Button>
          </div>
        )}
        {paymentsEnabled && activePlanId && !isCancelled && (
          <Button
            theme="text"
            className="text-pale-red"
            onClick={() => setIsCancelOpen(true)}
          >
            Cancel subscription
          </Button>
        )}
      </div>

      <ConfirmDialog
        isOpen={isCancelOpen}
        isLoading={cancelMutation.isPending}
        onOpenChange={setIsCancelOpen}
        title="Cancel subscription"
        description="Your plan will stay active until the end of the paid period, then it won't renew."
        cancelText="Keep subscription"
        confirmText="Cancel subscription"
        onCancel={() => setIsCancelOpen(false)}
        onConfirm={handleCancel}
      />
    </Layout>
  );
};
